// Visual block parser — synchronous, zero imports, zero mermaid.
// parseAndRenderVisuals : string → string  (placeholder HTML for the editor)
// parseFigureBlocks     : string → array   (structured data for React rendering)

// ── Field extractors ─────────────────────────────────────────────────────────

// Handles both same-line (KEY: value) and next-line (KEY:\nvalue) formats.
function getField(block, key) {
  // Same-line: KEY: value on one line
  const sameLine = block.match(new RegExp(`${key}:\\s*([^\\n]+)`, 'i'))
  if (sameLine?.[1]?.trim()) return sameLine[1].trim()
  // Next-line: KEY:\n<content on next line>
  const nextLine = block.match(new RegExp(`${key}:\\s*\\n([^\\n]+)`, 'i'))
  if (nextLine?.[1]?.trim()) return nextLine[1].trim()
  return ''
}

// Mermaid diagram openers — used to recognise a code body the model wrote
// without the CODE: label at all.
const MERMAID_HEAD = /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|journey|gantt|pie|mindmap|timeline|quadrantChart|gitGraph|xychart-beta|block-beta)\b/i

function stripFences(text) {
  const fence = text.match(/```[\w]*\r?\n([\s\S]*?)```/)
  return (fence ? fence[1] : text).trim()
}

// Extracts mermaid code. The model does not always put CODE: on its own line —
// it also writes `CODE: graph TD`, wraps the body in a ```mermaid fence, or
// drops the label entirely. Each of those used to yield no code, and a figure
// with no code was dropped, which is how a chapter asked for three diagrams and
// rendered one.
function getCodeBlock(block) {
  const codeField = block.match(/CODE:[ \t]*\r?\n?([\s\S]*?)(?=\n\s*CAPTION:|$)/i)
  if (codeField?.[1]?.trim()) return stripFences(codeField[1])

  const fence = block.match(/```[\w]*\r?\n([\s\S]*?)```/)
  if (fence?.[1]?.trim()) return fence[1].trim()

  const bare = block
    .replace(/^[ \t]*(?:TYPE|TITLE):[^\n]*\n?/gim, '')
    .replace(/\n\s*CAPTION:[\s\S]*$/i, '')
    .trim()
  return MERMAID_HEAD.test(bare) ? bare : ''
}

// ── Malformed placeholder normalizer ────────────────────────────────────────
// The model occasionally emits shorthand like [Diagram: Title] instead of a
// full [FIGURE_START] block. These cannot be rendered and must be stripped
// before they reach the editor or the DB.

const BAD_VISUAL_RE = /\[\s*(?:Diagram|Figure|Chart|Table|Placeholder)\s*:([^\]]*)\]/gi

// ── Unterminated block recovery ─────────────────────────────────────────────
// A stream that runs out of tokens mid-block leaves [FIGURE_START] with no
// [FIGURE_END]. Every block regex here is anchored on the closing marker, so
// such a block matched nothing and the diagram disappeared without a trace —
// the single largest cause of a chapter rendering fewer visuals than it asked
// for. Close the block at its own boundary so what was written still renders.
//
// It must only ever touch a block that is genuinely unterminated. The previous
// version ran on every block and ended the body at the first blank line, so a
// COMPLETE block that happened to contain one — the model routinely leaves a
// blank line before CAPTION:, or after TITLE: — was cut short and given a
// premature closing marker. What that did, depending on where the blank line
// fell: dropped the caption and spilled "CAPTION: … [FIGURE_END]" into the
// prose as raw text, or destroyed the figure outright and left the whole block
// visible as markup. A table with a blank line before its rows was lost the
// same way. Nothing downstream noticed, because the backend counts blocks on
// the raw text where they ARE properly closed — so it recorded three figures
// delivered while the chapter rendered one, and no repair call ever fired.
// Whether a visual survived came down to where the model happened to press
// Enter, which is exactly the inconsistency users reported.

// One block start, and everything up to the next block start (or the end).
const BLOCK_SEGMENT_RE = /\[(FIGURE|TABLE)_START\]([\s\S]*?)(?=\[(?:FIGURE|TABLE)_START\]|$)/g

// Where an unterminated block has to stop: the first blank line, so recovery
// can only ever reclaim the block's own lines, never the prose after it.
const BLANK_LINE_RE = /^([\s\S]*?)(\n[ \t]*\n[\s\S]*)$/

export function closeUnterminatedVisuals(content) {
  if (!content || !content.includes('_START]')) return content
  return content.replace(BLOCK_SEGMENT_RE, (segment, kind, rest) => {
    // Already closed — leave it exactly as written.
    if (rest.includes(`[${kind}_END]`)) return segment
    const split = rest.match(BLANK_LINE_RE)
    const body  = split ? split[1] : rest
    const tail  = split ? split[2] : ''
    return `[${kind}_START]${body}\n[${kind}_END]${tail}`
  })
}

// Everything that reads visual blocks normalises through this first, so a block
// can never be recovered on one path and dropped on another.
export function normaliseVisualMarkup(content) {
  return stripMalformedVisuals(closeUnterminatedVisuals(content))
}

export function stripMalformedVisuals(content) {
  if (!content) return content
  if (!BAD_VISUAL_RE.test(content)) return content
  BAD_VISUAL_RE.lastIndex = 0
  const cleaned = content.replace(BAD_VISUAL_RE, (match, title) => {
    console.warn(`[parseThesisVisuals] Stripped malformed visual placeholder: "${match.trim()}" — model did not use [FIGURE_START] format`)
    return '' // remove silently; surrounding prose remains intact
  })
  BAD_VISUAL_RE.lastIndex = 0
  return cleaned
}

// ── String replacement: produces placeholder HTML for the editor ─────────────

export function parseAndRenderVisuals(content) {
  if (!content) return content
  // Strip any malformed placeholders first
  const cleaned = normaliseVisualMarkup(content)
  if (!cleaned.includes('[FIGURE_START]') && !cleaned.includes('[TABLE_START]')) return cleaned
  try {
    let result = cleaned
    result = result.replace(
      /\[FIGURE_START\]([\s\S]*?)\[FIGURE_END\]/g,
      (_, inner) => `<p><em>[Diagram: ${getField(inner, 'TITLE') || 'Figure'}]</em></p>`,
    )
    result = result.replace(
      /\[TABLE_START\]([\s\S]*?)\[TABLE_END\]/g,
      (_, inner) => `<p><em>[Table: ${getField(inner, 'TITLE') || 'Table'}]</em></p>`,
    )
    return result
  } catch (err) {
    console.error('[parseThesisVisuals] error:', err)
    return cleaned
  }
}

// ── Tokenisation: visual blocks → inline <div> tokens + DB rows ─────────────
//
// Each block is parsed where it is replaced. The previous version parsed all
// figures into one array and then walked the blocks with a running index — so
// the moment any block failed to parse, every later figure was handed the wrong
// block's code and the last ones got none at all. A chapter with three diagrams
// came out with one rendered figure and two empty placeholders.

// Inline tokens already present in a piece of HTML.
//
// Two things need this. A resumed or re-processed chapter must continue its
// numbering from what is already in the content instead of restarting at 1 —
// restarting put two `fig_<key>_1` divs in one chapter, and every registry
// downstream is keyed by token, so the pair counted as one figure and the
// chapter reported fewer visuals than it visibly contained. And the figure
// table needs to know which rows the chapter still references, so the ones it
// no longer references can be removed.
const TOKEN_ID_RE = /<div[^>]*\bdata-thesis-figure="true"[^>]*\bdata-figure-id="([^"]*)"/gi

export function scanVisualTokens(html) {
  if (!html) return []
  const out = []
  let m
  TOKEN_ID_RE.lastIndex = 0
  while ((m = TOKEN_ID_RE.exec(html)) !== null) out.push(m[1])
  TOKEN_ID_RE.lastIndex = 0
  return out
}

// Which of the rows a chapter already has are no longer referenced by its
// content. Kept here, away from the DB call, so the decision to delete is
// testable on its own.
export function staleTokens(existingTokens, html) {
  const keep = new Set(scanVisualTokens(html))
  return (existingTokens || []).filter(t => t && !keep.has(t))
}

export function countVisualTokens(html) {
  const ids = scanVisualTokens(html)
  return {
    fig: ids.filter(t => t.startsWith('fig_')).length,
    tbl: ids.filter(t => t.startsWith('tbl_')).length,
  }
}

export function visualTokenDiv(token, type, code, title, caption) {
  return `<div data-thesis-figure="true" data-figure-id="${token}" data-figure-type="${type}" data-figure-code="${encodeURIComponent(code)}" data-figure-title="${encodeURIComponent(title)}" data-figure-caption="${encodeURIComponent(caption)}"></div>`
}

/**
 * Replace every [FIGURE_START]/[TABLE_START] block with an inline token div.
 * Blocks that carry nothing renderable are removed outright rather than left
 * behind as an empty figure box.
 *
 * `startAt` continues the numbering from visuals the chapter already holds —
 * pass `countVisualTokens(existingHtml)` when tokenising an addition to a
 * chapter rather than the whole of it.
 *
 * @returns {{ processed: string, rows: object[] }}
 */
export function tokenizeVisuals(rawMarkdown, { thesisId, chapterId, chapterNum = 1, chapterKey, startAt } = {}) {
  const rows = []
  // Figures and tables number independently. Sharing one counter numbered the
  // first table of a chapter with three figures "Table 1.4".
  const counts = { fig: Math.max(0, startAt?.fig | 0), tbl: Math.max(0, startAt?.tbl | 0) }
  // Appended visuals sort after the ones already in the chapter, not alongside.
  const orderBase = counts.fig + counts.tbl

  const take = (prefix, kind, parse) => (_, inner) => {
    const data = parse(inner)
    if (!data) return ''
    const tokenNum = ++counts[prefix]
    const token    = `${prefix}_${chapterKey}_${tokenNum}`
    rows.push({
      thesis_id:     thesisId,
      chapter_id:    chapterId,
      token,
      figure_number: `${chapterNum}.${tokenNum}`,
      title:         data.title   || '',
      caption:       data.caption || '',
      figure_type:   kind,
      code:          data.code    || '',
      display_order: orderBase + rows.length,
    })
    return visualTokenDiv(token, kind, data.code, data.title || '', data.caption || '')
  }

  const processed = normaliseVisualMarkup(rawMarkdown || '')
    .replace(/\[FIGURE_START\]([\s\S]*?)\[FIGURE_END\]/g, take('fig', 'mermaid', figureFromBlock))
    .replace(/\[TABLE_START\]([\s\S]*?)\[TABLE_END\]/g,   take('tbl', 'table',   tableFromBlock))

  return { processed, rows }
}

// ── Structured data for TABLE blocks ────────────────────────────────────────
// Returns [] when no valid table blocks are present — never throws.

// One block's inner text → structured data, or null when it carries nothing
// renderable. Everything that reads a visual block goes through these two, so a
// block can never be counted in one place and dropped in another.

export function figureFromBlock(inner) {
  const code = getCodeBlock(inner || '')
  if (!code) return null
  return { type: 'figure', title: getField(inner, 'TITLE'), code, caption: getField(inner, 'CAPTION') }
}

export function tableFromBlock(inner) {
  if (!inner) return null
  const code = inner
    .replace(/^\s*(?:TYPE|TITLE):[^\n]*\n?/gim, '')
    .replace(/\s*CAPTION:[\s\S]*$/i, '')
    .trim()
  if (!code.includes('|')) return null
  return { type: 'table', title: getField(inner, 'TITLE'), code, caption: getField(inner, 'CAPTION') }
}

export function parseTableBlocks(content) {
  if (!content) return []
  if (!content.includes('[TABLE_START]')) return []
  try {
    const cleaned = normaliseVisualMarkup(content)
    const blocks = []
    const re = /\[TABLE_START\]([\s\S]*?)\[TABLE_END\]/g
    let m
    while ((m = re.exec(cleaned)) !== null) {
      const tbl = tableFromBlock(m[1])
      if (tbl) blocks.push(tbl)
      else console.warn('[parseTableBlocks] Skipping table block with no parseable rows')
    }
    return blocks
  } catch (err) {
    console.error('[parseTableBlocks] error:', err)
    return []
  }
}

// ── Structured data: used by React to render MermaidDiagram components ───────
// Returns [] when no valid figure blocks are present — never throws.

// ── Raw-markdown visual counting (mirrors backend/src/utils/visualBudget.js's
// countVisuals/missingVisuals — kept in step for the same reason
// figureFromBlock/tableFromBlock are: a figure counted as delivered on one
// side and dropped on the other is exactly the bug class this file exists to
// prevent) ───────────────────────────────────────────────────────────────
// Built on figureFromBlock/tableFromBlock, so "does this block render" can
// never disagree between the count and the actual tokenisation.

const RAW_FIGURE_RE = /\[FIGURE_START\]([\s\S]*?)\[FIGURE_END\]/g
const RAW_TABLE_RE  = /\[TABLE_START\]([\s\S]*?)\[TABLE_END\]/g

function countRenderable(text, re, fromBlock) {
  let n = 0
  let m
  re.lastIndex = 0
  while ((m = re.exec(text)) !== null) if (fromBlock(m[1])) n += 1
  re.lastIndex = 0
  return n
}

/** How many complete, renderable visual blocks does this raw text actually contain? */
export function countVisuals(text) {
  const s = normaliseVisualMarkup(text || '')
  return {
    figures: countRenderable(s, RAW_FIGURE_RE, figureFromBlock),
    tables:  countRenderable(s, RAW_TABLE_RE, tableFromBlock),
  }
}

/** What a passage still owes its directive, after what it actually emitted. */
export function missingVisuals(directive, text) {
  const got = countVisuals(text)
  return {
    figures: Math.max(0, (directive?.figures | 0) - got.figures),
    tables:  Math.max(0, (directive?.tables | 0) - got.tables),
  }
}

export function parseFigureBlocks(content) {
  if (!content) return []
  // Close truncated blocks and strip malformed placeholders before parsing
  const cleaned = normaliseVisualMarkup(content)
  if (!cleaned.includes('[FIGURE_START]')) return []
  try {
    const blocks = []
    const re = /\[FIGURE_START\]([\s\S]*?)\[FIGURE_END\]/g
    let m
    while ((m = re.exec(cleaned)) !== null) {
      const fig = figureFromBlock(m[1])
      if (fig) blocks.push(fig)
      else console.warn('[parseFigureBlocks] Skipping figure block with no parseable code')
    }
    return blocks
  } catch (err) {
    console.error('[parseFigureBlocks] error:', err)
    return []
  }
}
